<script setup lang="ts">
import { Color } from '@previo/codebooks'
import { computed, ref } from 'vue'
import PIcon from '../icon/PIcon.vue'
import PSpace from '../space/PSpace.vue'
import { PSpaceSize } from '../space/types'
import { PHoverVariant } from './types'

type Props = {
  variant?: PHoverVariant
  readonly?: boolean
  disable?: boolean
  hideEditIcon?: boolean
}

type Emits = {
  click: [event: MouseEvent]
}

const props = withDefaults(defineProps<Props>(), {
  variant: PHoverVariant.STAND_ALONE,
  readonly: false,
  disable: false,
  hideEditIcon: false,
})

const emit = defineEmits<Emits>()

const isHovered = ref(false)

const isEditIconShow = computed<boolean>(() => {
  if (props.readonly || props.disable) {
    return false
  }

  if (props.hideEditIcon) {
    return false
  }

  return true
})

const isClickable = computed<boolean>(() => {
  return !props.disable && !props.readonly
})

const iconColor = computed<Color>(() => {
  return isHovered.value ? Color.GREY_500 : Color.GREY_400
})

const startEndSpaceSize = computed<PSpaceSize>(() => {
  return props.variant === PHoverVariant.FOR_TABLE ? PSpaceSize.ZERO : PSpaceSize.SM
})

const handleClick = (event: MouseEvent) => {
  if (!isClickable.value) return

  const selection = window.getSelection()
  if (selection && selection.toString().length > 0) return

  emit('click', event)
}
</script>

<template>
  <div
    :class="{
      'ds-hover--for-table': variant === PHoverVariant.FOR_TABLE,
      'p-cursor-pointer': isClickable,
      'ds-hover__disable': disable,
      'ds-hover__no-hover': readonly && !disable,
    }"
    class="ds-hover p-display-flex"
    @click="handleClick"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <p-space :mobile="startEndSpaceSize" />

    <div>
      <slot name="default" />
    </div>

    <template v-if="isEditIconShow">
      <p-space :mobile="PSpaceSize.XS" />
      <p-icon icon="pen" :color="iconColor" class="ds-hover-icon" :size="10" />
    </template>

    <p-space :mobile="startEndSpaceSize" />
  </div>
</template>

<style scoped lang="scss">
@import '../../assets/scss/shared/ds.variables.module';
@import '../../assets/scss/shared/brand.module';

.ds-hover {
  display: inline-flex;
  align-items: center;
  border-radius: $global-border-radius;

  &:hover {
    background-color: getColor('grey100');
  }

  // Table rows are already highlighted with grey100 on hover, so the cell needs a darker shade
  &--for-table {
    &:hover {
      background-color: getColor('grey200');
    }
  }

  &__no-hover {
    &:hover {
      background-color: transparent;
    }
  }

  &__disable {
    cursor: not-allowed;
    opacity: 0.64;
    pointer-events: none;

    &:hover {
      background-color: transparent;
    }
  }
}
</style>
